import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/hooks/useAuth'
import { usePageTitle } from '@/hooks/usePageTitle'
import { supabase } from '@/integrations/supabase/client'
import type { Database } from '@/integrations/supabase/types'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { Plus, Eye, Edit, Trash, Users } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

type Listing = Database['public']['Tables']['listings']['Row']
type Lead = Database['public']['Tables']['leads']['Row']

const Dashboard = () => {
  const [listings, setListings] = useState<Listing[]>([])
  const [leads, setLeads] = useState<Lead[]>([])
  const [userProfile, setUserProfile] = useState<any>(null)
  const [dataLoading, setDataLoading] = useState(true)
  const { user, loading: authLoading, signOut, getUserProfile } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  
  usePageTitle("Dashboard")

  useEffect(() => {
    if (authLoading) return
    if (!user) {
      navigate('/sign-in')
      return
    }
    fetchUserProfileAndData()
  }, [user, authLoading, navigate])

  const fetchUserProfileAndData = async () => {
    if (!user) return

    try {
      // First fetch user roles to determine access
      const roles = await getUserProfile(user.id)
      setUserProfile(roles)

      // Only fetch listings and leads if user has broker or seller role
      const userRoles = roles?.map((r: any) => r.role) || []
      const isBrokerOrSeller = userRoles.includes('broker') || userRoles.includes('seller')
      
      if (isBrokerOrSeller) {
        await fetchData()
      } else {
        // For buyers, just set empty arrays
        setListings([])
        setLeads([])
      }
    } catch (error) {
      console.error('Error fetching profile and data:', error)
    } finally {
      setDataLoading(false)
    }
  }

  const fetchData = async () => {
    if (!user) return

    try {
      // Fetch listings
      const { data: listingsData, error: listingsError } = await supabase
        .from('listings')
        .select('*')
        .eq('broker_id', user.id)
        .order('created_at', { ascending: false })

      if (listingsError) throw listingsError

      // Fetch leads for user's listings
      const { data: leadsData, error: leadsError } = await supabase
        .from('leads')
        .select(`
          *,
          listings!inner(title, broker_id)
        `)
        .eq('listings.broker_id', user.id)
        .order('created_at', { ascending: false })

      if (leadsError) throw leadsError

      setListings(listingsData || [])
      setLeads(leadsData || [])
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setDataLoading(false)
    }
  }

  const deleteListing = async (id: string) => {
    try {
      const { error } = await supabase
        .from('listings')
        .delete()
        .eq('id', id)

      if (error) throw error

      toast({
        title: 'Success',
        description: 'Listing deleted successfully',
      })
      
      fetchData()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    }
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  const getStatusBadge = (status: string) => {
    const variants = {
      draft: 'secondary',
      live: 'default',
      paused: 'outline',
      sold: 'destructive',
    } as const

    return (
      <Badge variant={variants[status as keyof typeof variants] || 'secondary'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    )
  }

  if (!user) {
    return null
  }

  // Show different dashboard based on user roles
  const userRoles = userProfile?.map((r: any) => r.role) || []
  const isBrokerOrSeller = userRoles.includes('broker') || userRoles.includes('seller')
  const primaryRole = userRoles.includes('admin') ? 'admin' : 
                     userRoles.includes('broker') ? 'broker' : 
                     userRoles.includes('seller') ? 'seller' : 'buyer'

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-primary">
              Welcome {primaryRole.charAt(0).toUpperCase() + primaryRole.slice(1)}!
            </h1>
            <p className="text-muted-foreground">
              {primaryRole === 'buyer' 
                ? "Welcome Buyer - Explore businesses for sale and manage your favorites"
                : primaryRole === 'seller'
                ? "Welcome Seller - Manage your business listings and leads"
                : primaryRole === 'broker'
                ? "Welcome Broker - Manage your listings, leads, and analytics"
                : "Welcome to your dashboard"}
            </p>
          </div>
          <div className="flex gap-2">
            {isBrokerOrSeller && (
              <Button asChild>
                <Link to="/list-business">
                  <Plus className="h-4 w-4 mr-2" />
                  New Listing
                </Link>
              </Button>
            )}
            <Button variant="outline" onClick={handleSignOut}>
              Sign Out
            </Button>
          </div>
        </div>

        {/* Stats Cards - Only show for brokers/sellers */}
        {isBrokerOrSeller ? (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-8">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Listings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{listings.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Live Listings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-primary">
                  {listings.filter(l => l.status === 'live').length}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Leads</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-secondary">{leads.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">New Leads</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-secondary">
                  {leads.filter(l => l.status === 'new').length}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="mb-8">
            <Card>
              <CardHeader>
                <CardTitle>
                  Welcome, {primaryRole.charAt(0).toUpperCase() + primaryRole.slice(1)}!
                </CardTitle>
                <CardDescription>
                  {primaryRole === 'buyer' 
                    ? "Explore businesses for sale and manage your favorites"
                    : primaryRole === 'seller'
                    ? "Welcome to your seller dashboard. You can list your business for sale here."
                    : "Welcome to your dashboard"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
                  <Button asChild size="lg">
                    <Link to="/explore">
                      <Eye className="h-4 w-4 mr-2" />
                      Browse Businesses
                    </Link>
                  </Button>
                  <Button variant="outline" size="lg" asChild>
                    <Link to="/favorites">
                      ❤️ View Favorites
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs - Only show for brokers/sellers */}
        {isBrokerOrSeller && (
          <Tabs defaultValue="listings" className="space-y-6">
            <TabsList>
              <TabsTrigger value="listings">My Listings</TabsTrigger>
              <TabsTrigger value="leads">Leads</TabsTrigger>
            </TabsList>

          <TabsContent value="listings">
            <Card>
              <CardHeader>
                <CardTitle>Business Listings</CardTitle>
                <CardDescription>
                  Manage your business listings and their status
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dataLoading ? (
                  <p className="text-center py-4">Loading listings...</p>
                ) : listings.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">No listings yet</p>
                    <Button asChild>
                      <Link to="/list-business">Create Your First Listing</Link>
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Listing #</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Sector</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {listings.map((listing) => (
                        <TableRow key={listing.id}>
                          <TableCell className="font-mono text-sm text-muted-foreground">{listing.listing_number || '-'}</TableCell>
                          <TableCell className="font-medium">{listing.title}</TableCell>
                          <TableCell>{listing.sector}</TableCell>
                          <TableCell>{getStatusBadge(listing.status)}</TableCell>
                          <TableCell>
                            {new Date(listing.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" size="sm" asChild>
                                <Link to="/broker-portal?section=listings">
                                  <Edit className="h-3 w-3" />
                                </Link>
                              </Button>
                              {listing.status === 'live' && (
                                <Button variant="outline" size="sm" asChild>
                                  <Link to={`/business/${listing.id}`}>
                                    <Eye className="h-3 w-3" />
                                  </Link>
                                </Button>
                              )}
                              <Button variant="outline" size="sm" onClick={() => deleteListing(listing.id)}>
                                <Trash className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="leads">
            <Card>
              <CardHeader>
                <CardTitle>Leads</CardTitle>
                <CardDescription>
                  Inquiries from potential buyers for your listings
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dataLoading ? (
                  <p className="text-center py-4">Loading leads...</p>
                ) : leads.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No leads yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leads.map((lead) => (
                        <TableRow key={lead.id}>
                          <TableCell className="font-medium">{lead.buyer_name}</TableCell>
                          <TableCell>{lead.buyer_email}</TableCell>
                          <TableCell>{lead.buyer_phone || 'Not provided'}</TableCell>
                          <TableCell className="max-w-xs truncate">
                            {lead.message || 'No message'}
                          </TableCell>
                          <TableCell>
                            {new Date(lead.created_at).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          </Tabs>
        )}
      </main>
      <Footer />
    </div>
  )
}

export default Dashboard